/* eslint-env es2020 */
import { ethers } from 'ethers';
import NftRevenueDistributorArtifact from '../abi/NftRevenueDistributor.json';
import {
  ensureWalletConnected,
  getUsdcAddress,
  usdcToUnits,
  unitsToUsdc,
  assertWalletOnBaseChain,
} from './blockchain';

const NFT_REWARD_ABI = NftRevenueDistributorArtifact.abi || NftRevenueDistributorArtifact;

let NFT_REWARD_ADDRESS = process.env.REACT_APP_NFT_REWARD_CONTRACT_ADDRESS || '';

export function setNftRewardContractAddress(address) {
  NFT_REWARD_ADDRESS = address || '';
}

export function getNftRewardContractAddress() {
  return NFT_REWARD_ADDRESS;
}

const ERC20_ABI = [
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function balanceOf(address owner) view returns (uint256)',
];

async function getSigner() {
  await ensureWalletConnected();
  if (typeof window.ethereum === 'undefined') throw new Error('MetaMask is not installed');
  const provider = new ethers.BrowserProvider(window.ethereum);
  return provider.getSigner();
}

export async function getNftRewardContract() {
  if (!NFT_REWARD_ADDRESS) throw new Error('NFT reward contract address not set');
  const signer = await getSigner();
  return new ethers.Contract(NFT_REWARD_ADDRESS, NFT_REWARD_ABI, signer);
}

export async function getNftRewardContractReadOnly() {
  if (!NFT_REWARD_ADDRESS) throw new Error('NFT reward contract address not set');
  const rpc = process.env.REACT_APP_RPC_URL || 'https://mainnet.base.org';
  const provider = new ethers.JsonRpcProvider(rpc);
  return new ethers.Contract(NFT_REWARD_ADDRESS, NFT_REWARD_ABI, provider);
}

async function ensureUsdcAllowanceForReward(requiredUnits) {
  const req = BigInt(requiredUnits);
  if (req <= 0n) return;
  await assertWalletOnBaseChain();
  const signer = await getSigner();
  const owner = ethers.getAddress(await signer.getAddress());
  const spender = ethers.getAddress(NFT_REWARD_ADDRESS);
  const usdcAddr = getUsdcAddress();
  if (!usdcAddr) throw new Error('USDC address not set');
  const usdc = new ethers.Contract(usdcAddr, ERC20_ABI, signer);
  const [balance, allowance] = await Promise.all([usdc.balanceOf(owner), usdc.allowance(owner, spender)]);
  if (BigInt(balance) < req) {
    throw new Error(`Insufficient USDC. Need ${unitsToUsdc(req)}, have ${unitsToUsdc(balance)}`);
  }
  if (BigInt(allowance) >= req) return;
  const tx = await usdc.approve(spender, req + req / 20n + 1n);
  await tx.wait();
}

export async function fundNftRewardPool(amountUsd) {
  const amountUnits = usdcToUnits(amountUsd);
  await ensureUsdcAllowanceForReward(amountUnits);
  const contract = await getNftRewardContract();
  const tx = await contract.fundRewardPool(amountUnits);
  const receipt = await tx.wait();
  return receipt.hash;
}

export async function withdrawNftRewardUnallocated(amountUsd, toAddress) {
  const amountUnits = usdcToUnits(amountUsd);
  const contract = await getNftRewardContract();
  const tx = await contract.withdrawUnallocated(amountUnits, toAddress);
  const receipt = await tx.wait();
  return receipt.hash;
}

export async function setNftRewardAdmin(account, enabled) {
  const contract = await getNftRewardContract();
  const tx = await contract.setRewardAdmin(ethers.getAddress(account), !!enabled);
  const receipt = await tx.wait();
  return receipt.hash;
}

export async function setNftRewardClaimAuthSigner(signerAddress) {
  const contract = await getNftRewardContract();
  const tx = await contract.setClaimAuthSigner(ethers.getAddress(signerAddress));
  const receipt = await tx.wait();
  return receipt.hash;
}

export async function setNftRewardSuperAdmin(account) {
  const contract = await getNftRewardContract();
  const tx = await contract.setSuperAdmin(ethers.getAddress(account));
  const receipt = await tx.wait();
  return receipt.hash;
}

export async function commitNftRewardSnapshot({ snapshotId, contentHash, totalPoints, snapshotAt }) {
  const contract = await getNftRewardContract();
  const tx = await contract.commitSnapshot(
    BigInt(snapshotId),
    contentHash,
    BigInt(totalPoints),
    BigInt(snapshotAt)
  );
  const receipt = await tx.wait();
  return receipt.hash;
}

export async function finalizeNftRewardDistribution({
  distributionId,
  allocationsHash,
  totalAmount,
  totalPoints,
}) {
  const contract = await getNftRewardContract();
  const tx = await contract.finalizeDistribution(
    BigInt(distributionId),
    allocationsHash,
    BigInt(totalAmount),
    BigInt(totalPoints)
  );
  const receipt = await tx.wait();
  return receipt.hash;
}

export async function claimNftRewardWithAuth(amountWei, distributionId, nonce, deadline, signature) {
  const contract = await getNftRewardContract();
  const tx = await contract.claimWithAuth(
    BigInt(amountWei),
    BigInt(distributionId),
    nonce,
    BigInt(deadline),
    signature
  );
  const receipt = await tx.wait();
  return receipt.hash;
}

export async function getNftRewardPoolSnapshot() {
  if (!NFT_REWARD_ADDRESS) return null;
  try {
    const c = await getNftRewardContractReadOnly();
    const [funded, allocated, claimed, available] = await Promise.all([
      c.totalFunded(),
      c.totalAllocated(),
      c.totalClaimed(),
      c.availableToAllocate(),
    ]);
    return {
      totalFunded: unitsToUsdc(funded),
      totalAllocated: unitsToUsdc(allocated),
      totalClaimed: unitsToUsdc(claimed),
      availableToAllocate: unitsToUsdc(available),
    };
  } catch {
    return null;
  }
}

export async function isNftRewardAdmin(address) {
  if (!NFT_REWARD_ADDRESS || !address) return false;
  try {
    const c = await getNftRewardContractReadOnly();
    return !!(await c.rewardAdmins(ethers.getAddress(address)));
  } catch {
    return false;
  }
}
